"use client";

import React from "react";
import { ArrowRight, Microscope, Cpu, CircleCheck as CheckCircle2, FlaskConical, ChartLine as LineChart } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface ServiceCardItem {
  id: string;
  badge: string;
  title: string;
  description: string;
  link: string;
  actionText: string;
  iconName: "microscope" | "cpu";
  keyPoints: string[];
}
const serviceCards: ServiceCardItem[] = [{
  id: "app-dev",
  badge: "Method & Validation",
  title: "Application Development & Consultation",
  description: "Tailored spectroscopic and analytical method development, spectral library building, and regulatory transfer support for regulated pharmaceutical and industrial processes.",
  link: "/ApplicationConsultationPage",
  actionText: "Explore Application Support",
  iconName: "microscope",
  keyPoints: ["Custom NIR and Raman chemometric model building", "Method transfer and laboratory-to-process scale-up", "Direct guidance from analytical application specialists"]
}, {
  id: "pat-feasibility",
  badge: "Trial & Feasibility",
  title: "PAT Feasibility Studies",
  description: "De-risk real-time process analytical technology investments with structured sample testing, proof-of-concept trials, and clear suitability reporting before deployment.",
  link: "/FeasibilityStudiesPage",
  actionText: "Request Feasibility Study",
  iconName: "cpu",
  keyPoints: ["Off-line and inline spectral feasibility assessments", "Quantitative measurement precision and limit evaluation", "Comprehensive feasibility summary and hardware recommendation"]
}];
export default function Servicenavigation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="servicenavigationcards" data-nav="light" className="w-full bg-muted/30 py-16 md:py-24 border-y border-border">
      <div className="page-container">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-medium mb-3">
            <FlaskConical className="size-3.5 text-primary" />
            Specialist Services
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            Targeted Technical Capabilities
          </h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed">
            From initial spectral suitability testing to full inline deployment and chemometric modeling, we help teams solve complex measurement challenges.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {serviceCards.map((card, index) => <motion.div key={card.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: index * 0.1
        }} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-200">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        {card.iconName === "microscope" ? <Microscope className="size-5" /> : <Cpu className="size-5" />}
                      </div>
                      <Badge variant="secondary" className="font-normal text-xs">
                        {card.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl md:text-2xl font-semibold text-card-foreground">
                      {card.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {card.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2 pb-6">
                    <div className="border-t border-border/60 pt-4">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                        Included Support
                      </p>
                      <ul className="space-y-2.5">
                        {card.keyPoints.map((point, pIndex) => <li key={pIndex} data-index={pIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-0">
                  <Button asChild className="w-full justify-between group">
                    <Link to={card.link}>
                      <span>{card.actionText}</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        <div className="mt-12 p-4 md:p-6 rounded-xl bg-card border border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-secondary text-secondary-foreground hidden sm:flex">
              <LineChart className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-card-foreground">
                Need guidance on spectroscopy instrumentation?
              </p>
              <p className="text-xs text-muted-foreground">
                Speak directly with an instrumentation engineer about your specific sample requirements.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" asChild className="shrink-0">
            <Link to="/DiscussYourApplicationPage">
              Discuss Your Application
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}